// ── Resume clarity feedback ──────────────────────────────────────────────────
//
// Turns the classification signals we already compute (ambiguous / family /
// confidence) into an encouraging, actionable "improve your resume" message —
// or nothing at all when the resume is clear. Pure + framework-free so the API
// (which computes the signals) and the web UI (which renders the message) share
// one source of truth. NEVER critical: every message is framed as "do this to
// get better matches," not "your resume is bad."
/**
 * Map signals → level. Precedence is most-actionable first:
 *   1. ambiguous     → experience points in conflicting directions
 *   2. unrecognized  → no family could be placed (unrecognized titles / too sparse)
 *   3. sparse        → a family WAS placed but on thin, low-confidence evidence
 *   4. clear         → confident, single-direction resume → stay silent
 *
 * (2) and (3) are distinct: unrecognized has no family; sparse has one but weakly.
 */
export function deriveClarityLevel(s) {
    if (s.ambiguous)
        return "ambiguous";
    if (s.family === "unknown")
        return "unrecognized";
    if (s.confidence === "low")
        return "sparse";
    return "clear";
}
/** Copy for a level, or null when the resume is clear (show nothing). */
export function clarityMessage(level) {
    switch (level) {
        case "ambiguous":
            return {
                headline: "Clarify your target role",
                body: "Your experience spans several areas — naming your target role, and leading with it on your resume, will sharpen your matches.",
            };
        case "unrecognized":
            return {
                headline: "Help us place your field",
                body: "We couldn't pin down your primary field. Using standard role titles and listing your key skills will improve your match quality.",
            };
        case "sparse":
            return {
                headline: "Add a little more detail",
                body: "Your resume is light on detail — adding specific skills, technologies, and clear role titles will improve your match quality.",
            };
        case "clear":
        default:
            return null;
    }
}
/** Convenience: signals → message (null when clear). */
export function clarityMessageFor(signals) {
    return clarityMessage(deriveClarityLevel(signals));
}
//# sourceMappingURL=resume-clarity.js.map